import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaArrowLeft, FaTimes, FaCamera, FaPaperclip, FaChevronDown } from 'react-icons/fa';
import MechInternalFilesField, { readFilesAsBase64 } from './MechInternalFilesField';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PRIORITIES = [
  { key: 'high', label: 'High', emoji: '🔴', value: 'high 🔴' },
  { key: 'medium', label: 'Medium', emoji: '🟡', value: 'medium 🟡' },
  { key: 'low', label: 'Low', emoji: '🟢', value: 'low 🟢' },
];
const TECHNICIANS = ['Ehsan', 'Gregor', 'Rowan', 'Ergi', 'Hooman', 'Kennedy', 'Mateo', 'Donny'];

async function compressImage(file, maxWidth = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const size = Math.round((dataUrl.length - 'data:image/jpeg;base64,'.length) * 0.75);
        resolve({ name: file.name, dataUrl, size });
      };
      img.onerror = reject; img.src = e.target.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

const INPUT_STYLE = { width: '100%', padding: '0.85rem', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '1rem', fontFamily: 'inherit', outline: 'none' };
const LABEL_STYLE = { display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '1rem', color: '#374151' };

function QuotationSection({ scopeOfWork, setScopeOfWork, materialsNeeded, setMaterialsNeeded, laborNeeded, setLaborNeeded, quotingTechnician, setQuotingTechnician, quotationEmail, setQuotationEmail, internalFiles, setInternalFiles, onAddInternalFiles }) {
  const hasContent = !!(scopeOfWork || materialsNeeded || laborNeeded || quotingTechnician || internalFiles.length);
  const [expanded, setExpanded] = useState(hasContent);
  useEffect(() => { if (hasContent) setExpanded(true); }, [hasContent]);
  return (
    <div style={{ border: '2px solid #e5e7eb', borderRadius: '12px', margin: '0 0 1.15rem', background: '#fafbfc', overflow: 'hidden' }}>
      <button type="button" onClick={() => setExpanded((e) => !e)}
        style={{ width: '100%', padding: '1rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{ fontSize: '1rem' }}>📝</span>
          <span style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>Make Quotation (For Hudson Office)</span>
          <span style={{ fontSize: '0.85rem', color: '#9ca3af', marginLeft: '0.25rem' }}>Optional</span>
        </div>
        <FaChevronDown style={{ color: '#6b7280', transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 1rem 1rem' }}>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Scope of Work</label><textarea value={scopeOfWork} onChange={(e) => setScopeOfWork(e.target.value)} placeholder="Scope of work..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 60 }} /></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Materials Needed</label><textarea value={materialsNeeded} onChange={(e) => setMaterialsNeeded(e.target.value)} placeholder="Materials, lead time, price..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 50 }} /></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Labor Needed</label><textarea value={laborNeeded} onChange={(e) => setLaborNeeded(e.target.value)} placeholder="Labor hours, # of techs..." rows={2} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 50 }} /></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Technician Quoting</label><select value={quotingTechnician} onChange={(e) => setQuotingTechnician(e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }}><option value="">Select technician...</option>{TECHNICIANS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div style={{ marginBottom: '0.85rem' }}><label style={LABEL_STYLE}>Email copy (optional)</label><input type="email" value={quotationEmail} onChange={(e) => setQuotationEmail(e.target.value)} placeholder="Technician email" style={INPUT_STYLE} /></div>

          <MechInternalFilesField files={internalFiles} setFiles={setInternalFiles} onAdd={onAddInternalFiles} />
        </div>
      )}
    </div>
  );
}

export default function MechIssueForm() {
  const { buildingId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedEquipmentId = searchParams.get('equipmentId') || '';
  const visitId = searchParams.get('visitId') || '';
  const issueId = searchParams.get('issueId') || '';
  const isEdit = !!issueId;
  const navigate = useNavigate();

  const [equipment, setEquipment] = useState([]);
  const [buildingName, setBuildingName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [equipmentId, setEquipmentId] = useState(preselectedEquipmentId);
  const [issueName, setIssueName] = useState('');
  const [issueDetails, setIssueDetails] = useState('');
  const [dateFound, setDateFound] = useState(todayISO());
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('ongoing');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [materialsNeeded, setMaterialsNeeded] = useState('');
  const [laborNeeded, setLaborNeeded] = useState('');
  const [quotingTechnician, setQuotingTechnician] = useState('');
  const [quotationEmail, setQuotationEmail] = useState('');
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [internalFiles, setInternalFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const photoRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      try {
        const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/building/${encodeURIComponent(buildingId)}/detail`);
        if (r.ok) {
          const data = await r.json();
          setEquipment(data.equipment || []);
          setBuildingName(data.building?.name || '');

          if (issueId) {
            const iss = (data.issues || []).find((i) => i.id === issueId);
            if (iss) {
              setEquipmentId(iss.equipmentId || '');
              setIssueName(iss.name || '');
              setIssueDetails(iss.details || '');
              setDateFound(iss.dateFound || todayISO());
              const pMatch = PRIORITIES.find((p) => p.key === iss.priority);
              setPriority(pMatch ? pMatch.value : '');
              setStatus(iss.status === 'resolved' ? 'resolved' : 'ongoing');
              setScopeOfWork(iss.scopeOfWork || '');
              setMaterialsNeeded(iss.materialsNeeded || '');
              setLaborNeeded(iss.laborNeeded || '');
              setQuotingTechnician(iss.quotingTechnician || '');
              setPhotos((iss.photoUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url })));
              setFiles((iss.fileUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url, name: url.split('/').pop() })));
              setInternalFiles((iss.internalFileUrls || []).filter(Boolean).map((url) => ({ type: 'existing', url, name: url.split('/').pop() })));
            }
          }
        }
      } catch (e) { console.error(e); }
      finally { setIsLoading(false); }
    }
    load();
  }, [buildingId, issueId]);

  useEffect(() => {
    if (preselectedEquipmentId) setEquipmentId(preselectedEquipmentId);
  }, [preselectedEquipmentId]);

  const handlePhotos = useCallback(async (fileList) => {
    for (const file of fileList) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const c = await compressImage(file);
        setPhotos((prev) => [...prev, { type: 'new', name: c.name, dataUrl: c.dataUrl, size: c.size }]);
      } catch (e) { console.error(e); }
    }
  }, []);

  const handleFileAttachments = useCallback((fileList) => readFilesAsBase64(fileList, setFiles), []);
  const handleInternalFiles = useCallback((fileList) => readFilesAsBase64(fileList, setInternalFiles), []);

  const goBack = () => {
    if (visitId) {
      navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}/visits/${encodeURIComponent(visitId)}/checklist`);
    } else {
      navigate(`/mech-schedule/buildings/${encodeURIComponent(buildingId)}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!equipmentId || !issueName.trim() || !issueDetails.trim() || !dateFound || !priority) { setErrorMsg('Please fill in all required fields.'); return; }
    if (photos.length === 0) { setErrorMsg('Please add at least one photo.'); return; }
    setSubmitting(true); setErrorMsg(''); setSuccessMsg('');
    try {
      const newPhotos = photos.filter((p) => p.type === 'new').map((p) => ({ name: p.name, type: 'image/jpeg', data: p.dataUrl.split(',')[1] }));
      const newFiles = files.filter((f) => f.type === 'new').map((f) => ({ name: f.name, type: f.mimeType, data: f.data }));
      const existingPhotoUrls = photos.filter((p) => p.type === 'existing').map((p) => p.url);
      const existingFileUrls = files.filter((f) => f.type === 'existing').map((f) => f.url);
      const newInternalFiles = internalFiles.filter((f) => f.type === 'new').map((f) => ({ name: f.name, type: f.mimeType, data: f.data }));
      const existingInternalFileUrls = internalFiles.filter((f) => f.type === 'existing').map((f) => f.url);

      const endpoint = isEdit ? '/api/mechanical/form/update-issue' : '/api/mechanical/form/submit-issue';
      const body = {
        ...(isEdit ? { issueId } : {}),
        buildingId, equipmentId, issueName, issueDetails, dateFound, priority,
        ...(visitId ? { visitId } : {}),
        status: status === 'resolved' ? 'Resolved' : 'Ongoing',
        scopeOfWork, materialsNeeded, laborNeeded, quotingTechnician,
        photos: newPhotos, files: newFiles,
        internalFiles: newInternalFiles, existingInternalFileUrls,
        ...(isEdit ? { existingPhotoUrls, existingFileUrls } : {}),
      };
      const r = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || 'Failed to save'); }
      const result = await r.json();
      // Always notify the internal recipients; the tech is added as a
      // recipient only when they entered their email.
      {
        const savedIssueId = isEdit ? issueId : result.objectId;
        const eqName = equipment.find((eq) => eq.id === equipmentId)?.name || '';
        try { await fetch(`${API_BASE_URL}/api/mechanical/form/email-issue-copy`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ issueId: savedIssueId, technicianEmail: quotationEmail.trim(), buildingId, buildingName, equipmentId, equipmentName: eqName, issueName, issueDetails, scopeOfWork, materialsNeeded, laborNeeded, quotingTechnician, priority, dateFound, isUpdate: isEdit }) }); } catch (_) {}
      }
      setSuccessMsg(isEdit ? 'Issue updated!' : 'Issue submitted!');
      setTimeout(() => goBack(), 800);
    } catch (err) { setErrorMsg(err.message); }
    finally { setSubmitting(false); }
  };

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem 1rem' }}>
      <Spinner animation="border" style={{ color: '#2563eb', marginBottom: '1rem' }}><span className="visually-hidden">Loading...</span></Spinner>
    </div>
  );

  const selectedEq = equipment.find((eq) => eq.id === equipmentId);

  return (
    <div style={{ padding: '1.5rem 1.25rem', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', fontWeight: 600, fontSize: '1rem', padding: 0, marginBottom: '1.25rem' }}>
          <FaArrowLeft /> Back
        </button>

        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', margin: '0 0 0.25rem' }}>{isEdit ? 'Edit Issue' : 'Report Issue'}</h1>
        <p style={{ fontSize: '1rem', color: '#6b7280', margin: '0 0 1.5rem' }}>{buildingName}</p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.15rem' }}>
            <label style={LABEL_STYLE}>Equipment *</label>
            <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} style={{ ...INPUT_STYLE, cursor: 'pointer' }} required>
              <option value="">Select equipment...</option>
              {equipment.map((eq) => <option key={eq.id} value={eq.id}>{eq.name}{eq.category ? ` (${eq.category})` : ''}</option>)}
            </select>
            {selectedEq?.photoUrl && (
              <div style={{ marginTop: '0.5rem' }}>
                <img src={selectedEq.photoUrl} alt="" style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', border: '1px solid #e5e7eb' }} />
              </div>
            )}
          </div>
          <div style={{ marginBottom: '1.15rem' }}>
            <label style={LABEL_STYLE}>Issue Name *</label>
            <input type="text" value={issueName} onChange={(e) => setIssueName(e.target.value)} placeholder="e.g., Dirty Filters" style={INPUT_STYLE} required />
          </div>
          <div style={{ marginBottom: '1.15rem' }}>
            <label style={LABEL_STYLE}>Issue Details *</label>
            <textarea value={issueDetails} onChange={(e) => setIssueDetails(e.target.value)} placeholder="Detailed description..." rows={3} style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 90 }} required />
          </div>

          <QuotationSection scopeOfWork={scopeOfWork} setScopeOfWork={setScopeOfWork} materialsNeeded={materialsNeeded} setMaterialsNeeded={setMaterialsNeeded}
            laborNeeded={laborNeeded} setLaborNeeded={setLaborNeeded} quotingTechnician={quotingTechnician} setQuotingTechnician={setQuotingTechnician}
            quotationEmail={quotationEmail} setQuotationEmail={setQuotationEmail}
            internalFiles={internalFiles} setInternalFiles={setInternalFiles} onAddInternalFiles={handleInternalFiles} />

          <div style={{ marginBottom: '1.15rem' }}><label style={LABEL_STYLE}>Date Found *</label><input type="date" value={dateFound} onChange={(e) => setDateFound(e.target.value)} style={INPUT_STYLE} required /></div>

          <div style={{ marginBottom: '1.15rem' }}>
            <label style={LABEL_STYLE}>Priority *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {PRIORITIES.map((p) => { const sel = priority === p.value; return (
                <button key={p.key} type="button" onClick={() => setPriority(p.value)} style={{ padding: '0.75rem 0.5rem', borderRadius: '10px', cursor: 'pointer', textAlign: 'center', fontWeight: sel ? 700 : 400, fontSize: '1rem', border: sel ? '2px solid #3b82f6' : '2px solid #e5e7eb', background: sel ? '#eff6ff' : '#fff', color: '#111827' }}>{p.label} {p.emoji}</button>
              ); })}
            </div>
          </div>

          <div style={{ marginBottom: '1.15rem' }}>
            <label style={LABEL_STYLE}>Photos *</label>
            <div onClick={() => photoRef.current?.click()}
              style={{ border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '1.25rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
              <FaCamera style={{ fontSize: '1.5rem', color: '#9ca3af', marginBottom: '0.3rem' }} />
              <div style={{ fontSize: '1rem', color: '#6b7280' }}>Tap to add photos</div>
            </div>
            <input ref={photoRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => { handlePhotos(Array.from(e.target.files)); e.target.value = ''; }} />
            {photos.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.65rem' }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative', width: 76, height: 76, borderRadius: 8, overflow: 'hidden', border: p.type === 'existing' ? '2px solid #3b82f6' : '2px solid #16a34a' }}>
                    <img src={p.type === 'existing' ? p.url : p.dataUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))} style={{ position: 'absolute', top: 2, right: 2, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.9)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.15rem' }}>
            <label style={LABEL_STYLE}>File Attachments</label>
            <div onClick={() => fileRef.current?.click()} style={{ border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '0.85rem', textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}>
              <FaPaperclip style={{ fontSize: '1.2rem', color: '#9ca3af', marginBottom: '0.2rem' }} />
              <div style={{ fontSize: '1rem', color: '#6b7280' }}>Tap to add files</div>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" multiple style={{ display: 'none' }} onChange={(e) => { handleFileAttachments(Array.from(e.target.files)); e.target.value = ''; }} />
            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.7rem', background: '#f3f4f6', borderRadius: '8px', fontSize: '0.95rem', color: '#374151' }}>
                    <FaPaperclip style={{ color: '#9ca3af', flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 700, fontSize: '1rem' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {errorMsg && <div style={{ padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{errorMsg}</div>}
          {successMsg && <div style={{ padding: '0.75rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.95rem', marginBottom: '0.75rem' }}>{successMsg}</div>}
          <button type="submit" disabled={submitting} style={{ width: '100%', padding: '0.9rem', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '1.1rem', cursor: submitting ? 'not-allowed' : 'pointer', background: submitting ? '#93c5fd' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Saving...' : (isEdit ? 'Update Issue' : 'Submit Issue')}
          </button>
        </form>
      </div>
    </div>
  );
}
